import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

import { FilterConfig } from '@/shared/types/table';

/**
 * Tipos de operadores de filtro disponibles
 */
export type FilterOperator = FilterConfig['operator'];

/**
 * Tipos de datos de columnas para determinar qué operadores mostrar
 */
export type ColumnDataType = 'text' | 'number' | 'date' | 'list';

/**
 * Tipo de compuerta lógica entre filtros
 */
export type LogicOperator = 'and' | 'or';

/**
 * Configuración extendida de filtro con metadata adicional
 */
export interface AdvancedFilterConfig {
  id: string;
  columnId: string;
  columnLabel: string;
  operator: FilterOperator;
  value: string | number | boolean | Date | (string | number)[] | null;
  value2?: string | number | boolean | Date | null; // Para operadores 'between'
  dataType?: ColumnDataType;
  label?: string; // Label del operador para mostrar al usuario
  logicOperator?: LogicOperator; // Compuerta lógica que conecta este filtro con el siguiente (solo aplica si hay un siguiente filtro)
}

/**
 * Opción de operador para el dropdown
 */
export interface FilterOperatorOption {
  value: FilterOperator;
  label: string;
  requiresValue: boolean;
  requiresSecondValue?: boolean; // Para operadores como "between"
  requiresOptions?: boolean; // Para operadores que necesitan una lista de opciones
  dataTypes: ColumnDataType[]; // Tipos de datos compatibles con este operador
}

/**
 * Opciones de columna para el dropdown de selección
 */
export interface FilterColumnOption {
  value: string;
  label: string;
  dataType: ColumnDataType;
  options?: Array<{ value: string | number; label: string }>; // Opciones disponibles para filtros de tipo 'list'
  icon?: IconDefinition; // Icono de FontAwesome para mostrar en el dropdown
}

/**
 * Configuración de filtros disponibles por columna
 */
export const FILTER_OPERATORS: FilterOperatorOption[] = [
  {
    value: 'equals',
    label: 'Es igual a',
    requiresValue: true,
    requiresOptions: false, // Para text, number, date usa input; para list usa dropdown
    dataTypes: ['text', 'number', 'list'],
  },
  {
    value: 'notEquals',
    label: 'Es diferente de',
    requiresValue: true,
    requiresOptions: false,
    dataTypes: ['text', 'number', 'list'],
  },
  {
    value: 'in',
    label: 'Es uno de',
    requiresValue: true,
    requiresOptions: true, // Requiere lista de opciones con checkboxes
    dataTypes: ['list'],
  },
  {
    value: 'contains',
    label: 'Contiene',
    requiresValue: true,
    dataTypes: ['text'],
  },
  {
    value: 'notContains',
    label: 'No contiene',
    requiresValue: true,
    dataTypes: ['text'],
  },
  {
    value: 'startsWith',
    label: 'Empieza con',
    requiresValue: true,
    dataTypes: ['text'],
  },
  {
    value: 'endsWith',
    label: 'Termina con',
    requiresValue: true,
    dataTypes: ['text'],
  },
  {
    value: 'gt',
    label: 'Mayor que',
    requiresValue: true,
    dataTypes: ['number'],
  },
  {
    value: 'gte',
    label: 'Mayor o igual que',
    requiresValue: true,
    dataTypes: [],
  },
  {
    value: 'lt',
    label: 'Menor que',
    requiresValue: true,
    dataTypes: ['number'],
  },
  {
    value: 'lte',
    label: 'Menor o igual que',
    requiresValue: true,
    dataTypes: [],
  },
  {
    value: 'between',
    label: 'Entre',
    requiresValue: true,
    requiresSecondValue: true,
    dataTypes: ['number', 'date'],
  },
  {
    value: 'isEmpty',
    label: 'Está vacío',
    requiresValue: false,
    dataTypes: ['text'],
  },
  {
    value: 'isNotEmpty',
    label: 'No está vacío',
    requiresValue: false,
    dataTypes: ['text'],
  },

  // Operadores específicos de fecha con labels amigables
  {
    value: 'afterDate',
    label: 'Después de',
    requiresValue: true,
    dataTypes: ['date'],
  },
  {
    value: 'beforeDate',
    label: 'Antes de',
    requiresValue: true,
    dataTypes: ['date'],
  },

  // Operadores de fechas relativas (no requieren valor)
  {
    value: 'thisMonth',
    label: 'Este mes',
    requiresValue: false,
    dataTypes: ['date'],
  },
  {
    value: 'lastMonth',
    label: 'Mes pasado',
    requiresValue: false,
    dataTypes: ['date'],
  },
  {
    value: 'thisYear',
    label: 'Este año',
    requiresValue: false,
    dataTypes: ['date'],
  },
  {
    value: 'lastYear',
    label: 'Año pasado',
    requiresValue: false,
    dataTypes: ['date'],
  },
];
